import {Middleware} from '@reduxjs/toolkit';
import {
  JwtToken,
  TokenRefreshResult,
  TokenRefresher,
  TransientRefreshCode,
  apiClient,
  configureTokenRefresher,
  getApiUrl,
  selectToken,
  storeToken,
  storeUserData,
} from '@smartmixin/core';
import type {Store} from 'redux';

/**
 * Tab coordination of the token refresh (FE #218).
 *
 * Every tab is its own JS context, and each used to refresh the shared refresh
 * token on its own. Once the backend makes rotated refresh tokens single-use
 * (JWT_BLACKLIST_AFTER_ROTATION), the tab that refreshes second is logged out.
 * The webapp counterpart of the mobile protocol
 * (apps/mobile/docs/session-token-protocol.md), simpler because the browser
 * offers a real lock across tabs that may be held across an await:
 *
 * - one record in localStorage, the token of record shared by every tab;
 * - the refresh runs under a Web Lock, so one tab refreshes at a time and the
 *   others then find the new pair instead of presenting the consumed token;
 * - logins and logouts write the record, and other tabs follow it, including
 *   when they come back from the back/forward cache or the background.
 *
 * A tab never logs out another tab's session by mistake: a tab that finds the
 * record has moved on (a logout, another account) ends only itself, with a
 * LOGOUT marked `fromOtherTab`, which writes nothing.
 *
 * Browsers without Web Locks or usable storage keep the uncoordinated path,
 * and write no record; the backend's `jwt.refresh` log counts them.
 */

export const RECORD_KEY = 'smartmixin:session';
export const LOCK_NAME = 'smartmixin:token-refresh';

/** Below core's 60 s refresher timeout: a tab never waits on a lock forever. */
export const LOCK_WAIT_MS = 45_000;

/**
 * A pair issued (`iat`, server time) at least this long after a tombstone was
 * written (`updatedAt`, device time) is a login whose record write was lost.
 */
const CLOCK_SKEW_SECONDS = 5 * 60;

export type TabSession = {
  v: 1;
  state: 'active' | 'signedOut';
  access: string | null;
  refresh: string | null;
  apiBase: string;
  updatedAt: number;
};

type RefreshResponse = {
  ok: boolean;
  status?: number;
  problem?: string | null;
  data?: unknown;
};

type Locks = {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
};

export type TabRefresherDependencies = {
  locks: Locks;
  post: (refreshToken: string, language: string) => Promise<RefreshResponse>;
  /** Ends this tab's session only (a LOGOUT that writes nothing). */
  endThisTab: () => void;
  now?: () => number;
};

const nowSeconds = () => Math.floor(Date.now() / 1000);
const withoutTrailingSlash = (url: string) => url.replace(/\/+$/, '');
const sameBackend = (record: TabSession) =>
  withoutTrailingSlash(record.apiBase) === withoutTrailingSlash(getApiUrl());

/** Whether this tab writes the record: set once coordination is installed. */
let coordinated = false;
let stopFollowing: (() => void) | null = null;

const parse = (json: string | null): TabSession | null => {
  if (!json) {
    return null;
  }
  try {
    const record = JSON.parse(json);
    return record?.v === 1 &&
      (record.state === 'active' || record.state === 'signedOut') &&
      typeof record.apiBase === 'string'
      ? record
      : null;
  } catch {
    return null;
  }
};

export const readTabSession = (): TabSession | null => {
  try {
    return parse(localStorage.getItem(RECORD_KEY));
  } catch {
    return null;
  }
};

/** Whether the record now holds `record`. */
const write = (record: TabSession): boolean => {
  try {
    localStorage.setItem(RECORD_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
};

const active = (token: JwtToken): TabSession => ({
  v: 1,
  state: 'active',
  access: token.accessToken,
  refresh: token.refreshToken,
  apiBase: withoutTrailingSlash(getApiUrl()),
  updatedAt: nowSeconds(),
});

const signedOut = (): TabSession => ({
  v: 1,
  state: 'signedOut',
  access: null,
  refresh: null,
  apiBase: withoutTrailingSlash(getApiUrl()),
  updatedAt: nowSeconds(),
});

type Claims = {user_id?: unknown; exp?: unknown; iat?: unknown};

const claimsOf = (token: string | null | undefined): Claims | null => {
  const payload = token?.split('.')[1];
  if (!payload) {
    return null;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Claims;
  } catch {
    return null;
  }
};

const sameUser = (a: string | null, b: string | null) => {
  const userA = claimsOf(a)?.user_id;
  return userA !== undefined && userA === claimsOf(b)?.user_id;
};

/** Inclusive, like core: valid while now <= exp - seconds. */
const validFor = (token: string | null, seconds: number, now: number) => {
  const exp = claimsOf(token)?.exp;
  return typeof exp === 'number' && now + seconds <= exp;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const transient = (code: TransientRefreshCode): TokenRefreshResult => ({
  status: 'transient',
  code,
});

const problemCode = (
  problem: string | null | undefined,
): TransientRefreshCode => {
  switch (problem) {
    case 'CONNECTION_ERROR':
    case 'NETWORK_ERROR':
      return 'connectionError';
    case 'TIMEOUT_ERROR':
      return 'timeoutError';
    case 'SERVER_ERROR':
      return 'serverError';
    default:
      return 'unknownError';
  }
};

/** Whether the record says this tab's session is over (logged out, or replaced). */
const endsThisTab = (record: TabSession, held: JwtToken) =>
  record.state === 'signedOut' || !sameUser(record.access, held.accessToken);

const LOGOUT_THIS_TAB = {type: 'LOGOUT', meta: {fromOtherTab: true}} as const;

export const createTabTokenRefresher = ({
  locks,
  post,
  endThisTab,
  now = nowSeconds,
}: TabRefresherDependencies): TokenRefresher => {
  return ({current, minValiditySeconds, language}) =>
    locks
      .request(LOCK_NAME, async (): Promise<TokenRefreshResult> => {
        let record = readTabSession();
        // No record yet, or one of another backend: this tab's pair is the
        // session here.
        if (!record || !sameBackend(record)) {
          record = active(current);
          if (!write(record)) {
            return transient('unknownError');
          }
        }
        // The session moved on in another tab. End this tab only: core then
        // finds its store changed and makes no alert and no second LOGOUT.
        if (endsThisTab(record, current)) {
          endThisTab();
          return {status: 'signedOut'};
        }
        if (
          record.access &&
          record.refresh &&
          validFor(record.access, minValiditySeconds, now())
        ) {
          return {
            status: 'ok',
            token: {accessToken: record.access, refreshToken: record.refresh},
          };
        }
        if (!record.refresh || !validFor(record.refresh, 0, now())) {
          return {status: 'expired'};
        }

        const presented = record.refresh;
        let response: RefreshResponse;
        try {
          response = await post(presented, language);
        } catch {
          return transient('unknownError');
        }

        // Logins and logouts in other tabs write without the lock: an outcome
        // only applies if the record still holds the token presented.
        const latest = readTabSession();
        if (
          !latest ||
          latest.state !== 'active' ||
          latest.refresh !== presented
        ) {
          if (latest && sameBackend(latest) && endsThisTab(latest, current)) {
            endThisTab();
            return {status: 'signedOut'};
          }
          return transient('unknownError');
        }

        const data = response.data as
          {access?: unknown; refresh?: unknown; detail?: unknown} | undefined;
        if (response.ok) {
          if (
            !isNonEmptyString(data?.access) ||
            !isNonEmptyString(data?.refresh)
          ) {
            return transient('unknownError');
          }
          const token = {accessToken: data.access, refreshToken: data.refresh};
          // Stored before it is handed out, or not handed out at all: the
          // record still holds the token just presented, which recovers.
          return write(active(token))
            ? {status: 'ok', token}
            : transient('unknownError');
        }
        if (response.status === 401) {
          const detail = (data?.detail ?? null) as {
            message?: unknown;
            code?: unknown;
          } | null;
          // A 401 without the backend's code is not the endpoint's verdict.
          if (!isNonEmptyString(detail?.code)) {
            return transient('unknownError');
          }
          return {
            status: 'rejected',
            details: isNonEmptyString(detail?.message)
              ? {message: detail.message, code: detail.code}
              : undefined,
          };
        }
        return transient(problemCode(response.problem));
      })
      .catch(() => transient('timeoutError'));
};

export const refreshRequestHeaders = (language: string) => ({
  'X-SmartMixin-Context': 'UI',
  'Accept-Language': language,
  'X-SmartMixin-Refresh-Coordination': '1',
});

type RefreshClient = {
  post: (
    url: string,
    data: object,
    config: {headers: Record<string, string>},
  ) => Promise<unknown>;
};

/** `POST auth/token/refresh/`, announcing itself as coordinated. */
export const postRefresh = (
  refreshToken: string,
  language: string,
  client: RefreshClient = apiClient(getApiUrl()) as unknown as RefreshClient,
) =>
  client.post(
    'auth/token/refresh/',
    {refresh: refreshToken},
    {headers: refreshRequestHeaders(language)},
  ) as Promise<RefreshResponse>;

type LogoutAction = {type: 'LOGOUT'; meta?: {fromOtherTab?: boolean}};

const isLogout = (action: unknown): action is LogoutAction =>
  (action as {type?: unknown})?.type === 'LOGOUT';

/**
 * Logins and logouts into the record, once coordination is installed.
 *
 * A logout writes the tombstone only if this tab held the session the record
 * describes: a tab whose session already moved on (a 401 from a request that
 * went out after another tab's login, say) must not end that login. Never a
 * LOGOUT that followed another tab, nor `storeToken` (a refresh, already
 * written under the lock).
 */
export const tabSessionMiddleware: Middleware = (api) => (next) => (action) => {
  const held = coordinated ? selectToken(api.getState()) : null;
  const result = next(action);
  if (!coordinated) {
    return result;
  }
  if (storeUserData.match(action)) {
    if (action.payload.token) {
      write(active(action.payload.token));
    }
  } else if (isLogout(action) && !action.meta?.fromOtherTab && held) {
    const record = readTabSession();
    if (
      !record ||
      !sameBackend(record) ||
      (record.state === 'active' && sameUser(record.access, held.accessToken))
    ) {
      write(signedOut());
    }
  }
  return result;
};

/** What the record says for a tab holding `held`: follow it, never sign in. */
const followRecord = (store: Store, record: TabSession | null) => {
  const held = selectToken(store.getState());
  if (!held || !record || !sameBackend(record)) {
    return;
  }
  if (endsThisTab(record, held)) {
    store.dispatch(LOGOUT_THIS_TAB);
    return;
  }
  if (record.access && record.refresh && record.refresh !== held.refreshToken) {
    store.dispatch(
      storeToken({accessToken: record.access, refreshToken: record.refresh}),
    );
  }
};

/**
 * Follow the record when another tab changes it, and catch up on what a tab
 * missed while frozen (back/forward cache) or in the background. A signed-out
 * tab is never signed in from here.
 */
export const followOtherTabs = (store: Store, target: Window = window) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key === RECORD_KEY) {
      followRecord(store, parse(event.newValue));
    }
  };
  const catchUp = () => followRecord(store, readTabSession());
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      catchUp();
    }
  };
  const onVisibility = () => {
    if (target.document.visibilityState === 'visible') {
      catchUp();
    }
  };
  target.addEventListener('storage', onStorage);
  target.addEventListener('pageshow', onPageShow);
  target.document.addEventListener('visibilitychange', onVisibility);
  return () => {
    target.removeEventListener('storage', onStorage);
    target.removeEventListener('pageshow', onPageShow);
    target.document.removeEventListener('visibilitychange', onVisibility);
  };
};

/**
 * At launch (PersistGate's onBeforeLift), bring the rehydrated store in line
 * with the record: the persisted root can lag it, since tabs flush their whole
 * root on a timer that background tabs slow down.
 *
 * - no record (first coordinated launch): seed it with the store's pair;
 * - a logout after the stored pair was issued: follow it (unless the pair was
 *   issued well after it, a login whose record write was lost: write it back);
 * - another account: end this tab;
 * - same account, newer pair: adopt it.
 */
export const reconcileTab = (store: Store) => {
  if (!coordinated) {
    return;
  }
  const held = selectToken(store.getState());
  const record = readTabSession();
  if (!held) {
    return;
  }
  if (!record || !sameBackend(record)) {
    write(active(held));
    return;
  }
  if (record.state === 'signedOut') {
    const iat = claimsOf(held.refreshToken)?.iat;
    if (
      typeof iat === 'number' &&
      iat > record.updatedAt + CLOCK_SKEW_SECONDS
    ) {
      write(active(held));
    } else {
      store.dispatch(LOGOUT_THIS_TAB);
    }
    return;
  }
  const recordExp = claimsOf(record.refresh)?.exp;
  const heldExp = claimsOf(held.refreshToken)?.exp;
  if (!sameUser(record.access, held.accessToken)) {
    store.dispatch(LOGOUT_THIS_TAB);
  } else if (
    record.access &&
    record.refresh &&
    record.refresh !== held.refreshToken &&
    typeof recordExp === 'number' &&
    typeof heldExp === 'number' &&
    recordExp >= heldExp
  ) {
    store.dispatch(
      storeToken({accessToken: record.access, refreshToken: record.refresh}),
    );
  }
};

const storageUsable = () => {
  try {
    const probe = `${RECORD_KEY}:probe`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

type WebLocks = {
  request<T>(
    name: string,
    options: {signal?: AbortSignal},
    callback: () => Promise<T>,
  ): Promise<T>;
};

/**
 * Installs the coordination where the browser offers Web Locks and usable
 * storage. Returns whether it did.
 */
export const coordinateTabs = (
  store: Store,
  locks: WebLocks | undefined = typeof navigator !== 'undefined'
    ? (navigator.locks as unknown as WebLocks | undefined)
    : undefined,
  target: Window | undefined = typeof window !== 'undefined'
    ? window
    : undefined,
): boolean => {
  if (!locks || !target || !storageUsable()) {
    return false;
  }
  coordinated = true;
  configureTokenRefresher(
    createTabTokenRefresher({
      locks: {
        request: (name, callback) =>
          locks.request(
            name,
            {signal: AbortSignal.timeout(LOCK_WAIT_MS)},
            callback,
          ),
      },
      post: (refreshToken, language) => postRefresh(refreshToken, language),
      endThisTab: () => store.dispatch(LOGOUT_THIS_TAB),
    }),
  );
  stopFollowing = followOtherTabs(store, target);
  return true;
};

/** Tests only: undo `coordinateTabs`' module state. */
export const resetTabCoordinationForTests = () => {
  coordinated = false;
  stopFollowing?.();
  stopFollowing = null;
  configureTokenRefresher(null);
};
